import { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Swal from "sweetalert2";
import { generateFileUrl, userRequest } from "../../requestMethods";

// Só JPEG entra em post de foto do Instagram (a Graph API recusa outros).
const isPostableImage = (file) => {
  if (typeof file !== "string") return false; // só fotos já salvas (URL pública)
  const ext = file.split("?")[0].split(".").pop().toLowerCase();
  return ext === "jpg" || ext === "jpeg";
};

const buildCaption = (product) => {
  if (!product) return "";
  const parts = [];
  if (product.title) parts.push(product.title.trim());
  const body = product.shortDesc || product.desc;
  if (body) parts.push(String(body).trim());
  return parts.join("\n\n").slice(0, 2200);
};

// Modal para escolher fotos + legenda e publicar no Instagram.
export default function InstagramPostModal({
  open,
  onClose,
  companyId,
  productId,
  product,
  files,
}) {
  const [caption, setCaption] = useState("");
  const [selected, setSelected] = useState({});
  const [status, setStatus] = useState({ connected: false });
  const [publishing, setPublishing] = useState(false);

  // Fotos elegíveis (JPEG já salvas). Instagram aceita até 10.
  const images = useMemo(() => {
    const arr = Array.isArray(files) ? files : Object.values(files || {});
    return arr.filter(isPostableImage).slice(0, 20);
  }, [files]);

  useEffect(() => {
    if (!open) return;
    setCaption(buildCaption(product));
    // pré-seleciona as 10 primeiras (limite do carrossel)
    const init = {};
    images.slice(0, 10).forEach((img) => {
      init[img] = true;
    });
    setSelected(init);
    userRequest
      .get(`${companyId}/instagram/status`)
      .then((res) => setStatus(res.data || { connected: false }))
      .catch(() => setStatus({ connected: false }));
  }, [open, product, images, companyId]);

  const selectedUrls = images.filter((img) => selected[img]);

  const toggle = (img) => {
    setSelected((prev) => {
      const next = { ...prev, [img]: !prev[img] };
      const count = Object.values(next).filter(Boolean).length;
      if (count > 10) {
        Swal.fire("Limite", "O Instagram permite no máximo 10 fotos por post.", "info");
        return prev;
      }
      return next;
    });
  };

  const handlePublish = async () => {
    if (selectedUrls.length === 0) {
      Swal.fire("Selecione fotos", "Escolha ao menos uma foto para publicar.", "info");
      return;
    }
    try {
      setPublishing(true);
      const res = await userRequest.post(
        `${companyId}/instagram/publish/${productId}`,
        {
          images: selectedUrls.map(generateFileUrl),
          caption,
        },
      );
      const { count, type } = res.data || {};
      onClose();
      Swal.fire(
        "Publicado!",
        `Post ${type === "carousel" ? "em carrossel" : "único"} com ${count} foto(s) enviado ao Instagram.`,
        "success",
      );
    } catch (e) {
      Swal.fire(
        "Não foi possível publicar",
        (e.response && e.response.data && e.response.data.message) ||
          "Erro ao publicar no Instagram.",
        "error",
      );
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Postar no Instagram</DialogTitle>
      <DialogContent dividers>
        {!status.connected ? (
          <div style={{ padding: "8px 0" }}>
            Nenhuma conta Instagram vinculada. Vá em{" "}
            <strong>Configurações da empresa → Instagram</strong> e conecte uma
            conta Profissional antes de publicar.
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              Publicando como <strong>@{status.username}</strong>. Selecione as
              fotos ({selectedUrls.length}/10):
            </div>
            {images.length === 0 ? (
              <div style={{ color: "#a33" }}>
                Nenhuma foto JPEG salva disponível. Salve o produto com fotos
                JPEG antes de postar (o Instagram não aceita vídeos nem PNG).
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                  gap: 8,
                  marginBottom: 16,
                }}
              >
                {images.map((img) => {
                  const on = !!selected[img];
                  return (
                    <div
                      key={img}
                      onClick={() => toggle(img)}
                      style={{
                        position: "relative",
                        cursor: "pointer",
                        borderRadius: 8,
                        overflow: "hidden",
                        outline: on ? "3px solid #8a2be2" : "1px solid #ddd",
                      }}
                    >
                      <img
                        src={generateFileUrl(img)}
                        alt=""
                        style={{
                          width: "100%",
                          height: 96,
                          objectFit: "cover",
                          display: "block",
                          opacity: on ? 1 : 0.55,
                        }}
                      />
                      {on && (
                        <span
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            background: "#8a2be2",
                            color: "#fff",
                            borderRadius: "50%",
                            width: 20,
                            height: 20,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <TextField
              label="Legenda"
              multiline
              minRows={3}
              maxRows={10}
              fullWidth
              value={caption}
              onChange={(e) => setCaption(e.target.value.slice(0, 2200))}
              helperText={`${caption.length}/2200`}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={!status.connected || publishing || selectedUrls.length === 0}
          onClick={handlePublish}
        >
          {publishing ? "Publicando…" : "Publicar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
